import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import {
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent
} from 'primeng/autocomplete';
import { AuthService } from '../../auth/auth.service';
import { getAllReports } from '../../features/reports/report-definitions';
import { NotificationService, NotificationSeverity } from '../../core/notification.service';

/** A single searchable report entry surfaced by the header search box. */
interface ReportSearchEntry {
  title: string;
  divisionLabel: string;
  /** Router path to the report viewer, e.g. `/actuary/summary`. */
  route: string;
}

/** A cross-application launcher tile shown in the header's apps grid. */
export interface AppLink {
  label: string;
  /** PrimeIcon class, e.g. `pi pi-warehouse`. */
  icon: string;
  /** Destination for the tile (another AgriFinOps app, external URL, or route). */
  url: string;
  /** Accent colour for the tile icon/background. */
  color: string;
}

/**
 * Global application header (Hudson Crop & Livestock).
 *
 * Reused across every screen via the app shell. Hosts the report search
 * (navigates via {@link Router}), the notification bell (backed by
 * {@link NotificationService}) and the account menu (reads the signed-in user
 * from {@link AuthService} and drives sign-out). Remaining actions are surfaced
 * as outputs so the host decides what they do.
 */
@Component({
  selector: 'app-header',
  standalone: false,
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent {
  /**
   * Environment badge text (e.g. "DEV", "QA"). Empty hides the badge —
   * production builds pass an empty string.
   */
  @Input() environmentLabel = '';

  /** Tiles rendered in the apps launcher popover. */
  @Input() apps: AppLink[] = [
    { label: 'Livestock', icon: 'pi pi-warehouse', url: '/', color: '#2E7FA6' }
  ];

  /** Emitted when the search icon is clicked. */
  @Output() searchClick = new EventEmitter<void>();
  /** Emitted when the notification bell is clicked. */
  @Output() notificationsClick = new EventEmitter<void>();
  /** Emitted when "Profile" is chosen from the account menu. */
  @Output() profileClick = new EventEmitter<void>();
  /** Emitted when "Calendar" is chosen from the account menu. */
  @Output() calendarClick = new EventEmitter<void>();

  /** Display name of the signed-in user. */
  readonly username: string;

  /** Model for the PrimeNG account menu (Profile / Calendar / Log out). */
  readonly userMenuItems: MenuItem[];

  /** Whether the report search box is expanded in the header. */
  searchOpen = false;
  /** Two-way bound value of the search input. */
  searchQuery = '';
  /** Report entries matching the current query, shown as autocomplete suggestions. */
  searchSuggestions: ReportSearchEntry[] = [];

  /** Pre-built, case-insensitive search index of every available report. */
  private readonly searchIndex: ReportSearchEntry[] = getAllReports().map((r) => ({
    title: r.title,
    divisionLabel: r.division === 'actuary' ? 'Actuary' : 'Finance',
    route: `/${r.division}/${r.key}`
  }));

  constructor(
    private readonly auth: AuthService,
    private readonly router: Router,
    readonly notifications: NotificationService
  ) {
    this.username = this.auth.getUsername() ?? 'Account';
    this.userMenuItems = [
      { label: 'Profile', icon: 'pi pi-user', command: () => this.profileClick.emit() },
      { label: 'Calendar', icon: 'pi pi-calendar', command: () => this.calendarClick.emit() },
      { separator: true },
      { label: 'Log out', icon: 'pi pi-sign-out', command: () => this.logout() }
    ];
  }

  /** Shows/hides the report search box. Notifies the host and clears state when closing. */
  toggleSearch(): void {
    this.searchOpen = !this.searchOpen;
    this.searchClick.emit();
    if (!this.searchOpen) {
      this.resetSearch();
    }
  }

  /** Filters the report index by title for the typed query (empty query → no suggestions). */
  onSearchComplete(event: AutoCompleteCompleteEvent): void {
    const query = event.query.trim().toLowerCase();
    if (!query) {
      this.searchSuggestions = [];
      return;
    }
    this.searchSuggestions = this.searchIndex.filter((entry) =>
      entry.title.toLowerCase().includes(query)
    );
  }

  /** Navigates to the chosen report and collapses the search box. */
  onReportSelected(event: AutoCompleteSelectEvent): void {
    const entry = event.value as ReportSearchEntry | undefined;
    if (entry?.route) {
      void this.router.navigateByUrl(entry.route);
    }
    this.searchOpen = false;
    this.resetSearch();
  }

  private resetSearch(): void {
    this.searchQuery = '';
    this.searchSuggestions = [];
  }

  /**
   * Opens the notifications panel. The badge is intentionally NOT cleared here —
   * notifications persist until the user explicitly hits "Clear all" in the panel.
   */
  onBellClick(): void {
    this.notificationsClick.emit();
  }

  /** PrimeIcon class for a notification's severity, used in the bell panel. */
  iconFor(severity: NotificationSeverity): string {
    switch (severity) {
      case 'success':
        return 'pi-check-circle';
      case 'error':
        return 'pi-times-circle';
      case 'warn':
        return 'pi-exclamation-triangle';
      default:
        return 'pi-info-circle';
    }
  }

  /** Ends the MSAL session and redirects to the post-logout URI. */
  logout(): void {
    void this.auth.logout();
  }
}
